import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import Button from 'components/UI/Button';
import { Moment } from 'moment-timezone';
import { FunctionComponent } from 'react';
import { reduceResolution, wbp } from 'Theme';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { useHistory, useLocation } from 'react-router-dom';
import { CheckoutType } from './Checkout';

interface PaymentDoneProps {
    total: string;
    date: Moment;
    type: CheckoutType;
    remainingBalance?: number;
    className?: string;
    onClose?: () => void;
    onViewBooking?: () => void;
    onViewInvoice?: () => void;
    onPayRemainingBalance?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',

            [theme.breakpoints.down('sm')]: {
                padding: '0px 16px'
            }
        },
        closeButton: {
            top: 10,
            right: 10,
            zIndex: 10,
            border: 0,
            color: '#000',
            fontSize: 20,
            position: 'absolute',
            backgroundColor: 'transparent',

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        mobileWrapper: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',

            [theme.breakpoints.down('sm')]: {
                flex: 1
            }
        },
        icon: {
            color: '#5E8677',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(94)}px !important`,
                height: `${reduceResolution(94)}px !important`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '94px !important',
                height: '94px !important'
            }
        },
        title: {
            fontSize: 26,
            lineHeight: 1,
            fontWeight: 600,
            color: '#222222',
            marginTop: 24,

            [theme.breakpoints.up('md')]: {
                fontSize: 32,
                marginTop: 15
            }
        },
        date: {
            color: '#000',
            fontSize: 16,
            lineHeight: 1.4,
            fontWeight: 400,
            marginTop: 12,

            [theme.breakpoints.down('sm')]: {
                maxWidth: 250,
                marginLeft: 'auto',
                marginRight: 'auto',
                textAlign: 'center'
            },
            [theme.breakpoints.up('md')]: {
                fontSize: 15,
                marginTop: 15
            }
        },
        total: {
            fontSize: 22,
            lineHeight: 1,
            fontWeight: 500,
            color: '#92B4A7',
            marginTop: 30,

            [theme.breakpoints.up('md')]: {
                fontSize: 28,
                marginTop: 25
            }
        },
        paid: {
            fontSize: 16,
            fontWeight: 600,
            color: '#2C2C2C',
            marginTop: 8,

            [theme.breakpoints.up(wbp)]: {
                fontSize: 16,
                fontWeight: 500
            }
        },
        button: {
            [theme.breakpoints.down('sm')]: {
                'height': 55,
                'width': '100%',
                'marginBottom': 24,

                '& span': {
                    fontSize: 18
                }
            },
            [theme.breakpoints.up('md')]: {
                marginTop: '35px'
            }
        },
        customButton: {
            [theme.breakpoints.up('md')]: {
                width: '80%'
            }
        },
        secondaryButtonsRow: {
            gap: 12,
            width: '80%',
            display: 'flex',
            flexDirection: 'row',

            [theme.breakpoints.down('sm')]: {
                gap: 8,
                marginBottom: 24,
                width: '100%'
            },
            [theme.breakpoints.up('md')]: {
                marginTop: '16px'
            }
        },
        secondaryButton: {
            flex: 1,
            marginTop: '0 !important',
            marginBottom: '0 !important',

            [theme.breakpoints.down('sm')]: {
                'height': 55,
                'marginBottom': '0 !important',

                '& span': {
                    fontSize: 18
                }
            }
        }
    })
);

export const PaymentDone: FunctionComponent<PaymentDoneProps> = props => {
    const classes = useStyles();

    const history = useHistory();
    const location = useLocation();

    const isSingleSale = props.type === 'singlesale';
    const isCompleted = Number(props.total) === 0;
    const title = isCompleted && props.type === 'booking' ? 'Booking Completed!' : 'Payment Successful!';
    const remainingBalance = props.remainingBalance ?? 0;

    const viewMembershipsHandler = () => {
        if (location.pathname === '/memberships') {
            window.location.reload();
        } else {
            history.push('/memberships');
        }
    };

    const viewTransactionsHandler = () => {
        history.push('/payments#TRANSACTIONS');
    };

    return (
        <Box className={classes.container}>
            <button
                className={classes.closeButton}
                onClick={props.onClose}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>

            <Box className={classes.mobileWrapper}>
                <FontAwesomeIcon
                    icon={faCircleCheck}
                    className={classes.icon}
                />
                <Typography className={classes.title}>{title}</Typography>
                <Typography className={classes.date}>{`Payment received on ${props.date.format(
                    'dddd, D MMM YYYY'
                )}`}</Typography>
                <Typography
                    className={classes.total}
                    id="payment-done-total"
                >
                    ${props.total}
                </Typography>
                <Typography className={classes.paid}>Total Paid</Typography>
            </Box>

            {isSingleSale && (
                <Button
                    color="black"
                    label="Done"
                    onClick={props.onClose}
                    className={classes.button}
                    id="payment-done-close"
                />
            )}

            {!isSingleSale && remainingBalance > 0 && (
                <>
                    <Button
                        color="black"
                        label="Pay Remaining Balance"
                        onClick={props.onPayRemainingBalance}
                        className={`${classes.button} ${classes.customButton}`}
                        id="payment-done-pay-remaining-balance"
                    />
                    <Box className={classes.secondaryButtonsRow}>
                        <Button
                            variant="secondary"
                            color="black"
                            label="View Transactions"
                            onClick={viewTransactionsHandler}
                            className={classes.secondaryButton}
                            id="payment-done-go-to-transactions"
                        />
                        {props.type === 'booking' && (
                            <Button
                                variant="secondary"
                                color="black"
                                label="View Booking"
                                onClick={props.onViewBooking ?? props.onClose}
                                className={classes.secondaryButton}
                                id="payment-done-go-to-bookings"
                            />
                        )}
                        {props.type === 'membership' && (
                            <Button
                                variant="secondary"
                                color="black"
                                label="View Membership"
                                onClick={viewMembershipsHandler}
                                className={classes.secondaryButton}
                                id="payment-done-go-to-membership"
                            />
                        )}
                    </Box>
                </>
            )}
            {!isSingleSale && remainingBalance === 0 && (
                <>
                    {props.onViewInvoice ? (
                        <Button
                            color="black"
                            label="View Invoice"
                            onClick={props.onViewInvoice}
                            className={classes.button}
                            id="payment-done-go-to-invoice"
                        />
                    ) : (
                        <Button
                            color="black"
                            label="View Transactions"
                            onClick={viewTransactionsHandler}
                            className={classes.button}
                            id="payment-done-go-to-transactions"
                        />
                    )}
                    {props.type === 'booking' && (
                        <Button
                            color="black"
                            label="View Booking"
                            onClick={props.onViewBooking ?? props.onClose}
                            className={classes.button}
                            id="payment-done-go-to-bookings"
                        />
                    )}
                    {props.type === 'membership' && (
                        <Button
                            color="black"
                            label="View Membership"
                            onClick={viewMembershipsHandler}
                            className={classes.button}
                            id="payment-done-go-to-membership"
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default PaymentDone;
